/**
 * Typed boundary for the deals Supabase RPCs.
 *
 * The browser Supabase client is typed `any` (see `@wtc/ui` `getBrowserSupabaseClient`),
 * so `supabase.rpc(...)` hands back untyped data. The deals list previously cast
 * that straight to `Deal[]` — which was a lie: these RPCs return a PARTIAL
 * projection of the `deals` table plus a window `total_count`, NOT a full row.
 * Casting fabricated the columns the functions never SELECT.
 *
 * Verified column shapes via `pg_get_function_result` (dev + prod, 2026-07):
 *   filter_deals_by_follow_up_status / filter_deals_with_scheduled_followup →
 *     id, title, description, company_name, company_id, stage, revenue, tags,
 *     assigned_users, close_date, close_probability, created_at, updated_at,
 *     total_count
 *   search_deals →
 *     id, title, description, company_name, company_id, stage, revenue, tags,
 *     assigned_users, relevance, total_count   (no timestamps / close_* fields)
 *
 * This module models those real shapes, validates the untyped payload at the
 * boundary, and maps each partial row onto the `Deal` shape the
 * `EntityHooks<Deal>` contract requires. Only columns the RPC actually returns
 * carry real values; the columns a projection omits take explicit, documented
 * neutral defaults (these list/search rows never feed recurring-revenue math,
 * where those fields matter).
 */

import type { Deal, DealStage } from "@/lib/types/database/deals";

/** Columns every deals RPC row carries. */
interface DealRpcCommon {
  id: string;
  title: string;
  description: string | null;
  company_name: string | null;
  company_id: string;
  /** `deals.stage` is a text column in these RPCs; narrowed to DealStage on map. */
  stage: string;
  /** BigFloat/numeric — may arrive as string; coerced via normalizeDeals. */
  revenue: number | string | null;
  tags: string[] | null;
  assigned_users: string[] | null;
  /** Window `count(*) OVER()` — identical on every row of the result set. */
  total_count: number;
}

/** Row shape of the follow-up filter RPCs. */
export interface DealFilterRpcRow extends DealRpcCommon {
  close_date: string | null;
  close_probability: number | null;
  created_at: string;
  updated_at: string;
}

/** Row shape of `search_deals` (FTS): adds relevance, omits timestamps + close_*. */
export interface DealSearchRpcRow extends DealRpcCommon {
  relevance: number;
}

/** WTC deals are USD; the RPC projections don't return the currency column. */
const DEFAULT_CURRENCY = "USD";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * Narrow the untyped `supabase.rpc()` payload to rows. Shallow by design — it
 * verifies the payload is an array of objects with a string `id` (the one field
 * every branch relies on), then trusts the DB-owned column shape. Rows that
 * fail the check are dropped rather than crashing a `.map` downstream.
 */
function toRpcRows<T extends DealRpcCommon>(payload: unknown): T[] {
  if (!Array.isArray(payload)) return [];
  return payload.filter(
    (row): row is T => isRecord(row) && typeof row.id === "string"
  );
}

export const toDealFilterRpcRows = (payload: unknown): DealFilterRpcRow[] =>
  toRpcRows<DealFilterRpcRow>(payload);

export const toDealSearchRpcRows = (payload: unknown): DealSearchRpcRow[] =>
  toRpcRows<DealSearchRpcRow>(payload);

/** Result-set total for the footer — the window count from any row. */
export function totalCountFromRpcRows(rows: readonly DealRpcCommon[]): number {
  return rows[0]?.total_count ?? rows.length;
}

function toNumber(value: number | string | null): number {
  if (value === null) return 0;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** Map a follow-up filter row onto `Deal` (present columns real, rest neutral). */
export function dealFilterRowToDeal(row: DealFilterRpcRow): Deal {
  return {
    id: row.id,
    company_id: row.company_id,
    company_name: row.company_name ?? undefined,
    title: row.title,
    description: row.description ?? undefined,
    revenue: toNumber(row.revenue),
    currency: DEFAULT_CURRENCY,
    stage: row.stage as DealStage,
    close_date: row.close_date ?? undefined,
    close_probability: row.close_probability ?? 0,
    is_recurring: false,
    is_committed: false,
    tags: row.tags ?? undefined,
    assigned_users: row.assigned_users ?? undefined,
    last_activity_at: row.updated_at,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

/** Map a `search_deals` row onto `Deal`. Timestamps aren't projected by FTS, so
 *  the date fields are left empty (these search rows render title/company/stage/
 *  revenue, not dates). */
export function dealSearchRowToDeal(row: DealSearchRpcRow): Deal {
  return {
    id: row.id,
    company_id: row.company_id,
    company_name: row.company_name ?? undefined,
    title: row.title,
    description: row.description ?? undefined,
    revenue: toNumber(row.revenue),
    currency: DEFAULT_CURRENCY,
    stage: row.stage as DealStage,
    close_probability: 0,
    is_recurring: false,
    is_committed: false,
    tags: row.tags ?? undefined,
    assigned_users: row.assigned_users ?? undefined,
    last_activity_at: "",
    created_at: "",
    updated_at: "",
  };
}
